import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SidebarTopbar from "../../components/layouts/layout";
import {
  FaList,
  FaCheckCircle,
  FaExclamationCircle,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { BiCarousel, BiImageAlt } from "react-icons/bi";
import { GrDocumentVideo, GrDocumentPpt, GrDocumentImage, GrDocumentSound } from "react-icons/gr";
import "./GenerationsHistory.css";

function GenerationsHistory() {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [searchQuery, setSearchQuery] = useState("");
  const [publications, setPublications] = useState([]);
  const [filteredPublications, setFilteredPublications] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [totalPublicationsCount, setTotalPublicationsCount] = useState(0);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPublicationId, setSelectedPublicationId] = useState(null);

  const [deleteResultModal, setDeleteResultModal] = useState(false);
  const [deleteResultSuccess, setDeleteResultSuccess] = useState(false);
  const [deleteResultMessage, setDeleteResultMessage] = useState("");

  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const location = useLocation();
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // Get user and organization data from localStorage
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const organizationData = JSON.parse(localStorage.getItem("organizationData") || "{}");

  // Check if user is "demo" - if so, no restrictions apply
  const isDemoUser = userData.Username === "demo";

  // Hide Action column for organization.id === 1 (unless demo user)
  const hideActionColumn = !isDemoUser && organizationData.id === 1;

  // Get allowed content types (only apply if not demo user)
  const allowedContentTypes = isDemoUser ? [] : (organizationData.allowed_content_types || []);

  // Helper function to check if a content type tab should be shown
  const isTabAllowed = (contentType) => {
    if (isDemoUser) return true; // Demo user sees all tabs
    if (allowedContentTypes.length === 0) return true; // If no restrictions, show all
    return allowedContentTypes.includes(contentType);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const filter = searchParams.get("filter") || "all";
    setActiveTab(filter);
    setCurrentPage(1);
    fetchPublications(1, filter, searchQuery);
  }, [location.search]);

  const fetchPublications = async (page = 1, tab = "all", search = "") => {
    try {
      const response = await fetch(
        `${BASE_URL}/getPublicationsWithFilesAndTasks?page=${page}&limit=${itemsPerPage}&tab=${tab}&search=${encodeURIComponent(search)}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch publications");
      }

      const data = await response.json();
      if (data && typeof data === "object" && !Array.isArray(data)) {
        setPublications(data.publications || []);
        setFilteredPublications(data.publications || []);
        setTotalPublicationsCount(data.total_count || 0);
      } else {
        // Fallback for backward compatibility
        const sortedData = data.sort((a, b) => b.id - a.id);
        setPublications(sortedData);
        setFilteredPublications(sortedData);
        setTotalPublicationsCount(sortedData.length);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    fetchPublications(1, tab, searchQuery);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPublications(1, activeTab, searchQuery);
  };

  const handlePreview = (id) => {
    navigate(`/publicationview/${id}`);
  };

  const handleDeleteClick = (id) => {
    setSelectedPublicationId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (!selectedPublicationId) return;

      const formData = new FormData();
      formData.append("publicationId", selectedPublicationId);

      const response = await fetch(`${BASE_URL}/deletePublication`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to delete publication");
      }

      const data = await response.json();
      console.log("Delete success:", data);

      setShowDeleteModal(false);

      setDeleteResultSuccess(true);
      setDeleteResultMessage("Your publication got deleted successfully");
      setDeleteResultModal(true);

      // Calculate correct page after deletion: if last item on page is deleted, go back 1 page
      const isLastItem = filteredPublications.length === 1;
      const newPage = (isLastItem && currentPage > 1) ? currentPage - 1 : currentPage;
      setCurrentPage(newPage);
      fetchPublications(newPage, activeTab, searchQuery);
    } catch (error) {
      console.error("Error deleting publication:", error);

      setShowDeleteModal(false);

      setDeleteResultSuccess(false);
      setDeleteResultMessage("Unable to delete your publication. Please try again");
      setDeleteResultModal(true);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedPublicationId(null);
  };

  const handleCloseResultModal = () => {
    setDeleteResultModal(false);
    setDeleteResultMessage("");
  };

  const renderFormatIcons = (userFiles, tasks = []) => {
    const iconOrder = [
      { type: "ppt", icon: GrDocumentPpt },
      { type: "video", icon: GrDocumentVideo },
      { type: "poster", icon: GrDocumentImage },
      { type: "audio", icon: GrDocumentSound },
    ];

    return iconOrder.map(({ type, icon: Icon }) => {
      // Check if there's a completed task for this type
      const hasCompletedTask = tasks.some(
        (task) => task.type === type && task.status === "completed"
      );
      const color = hasCompletedTask ? "#1e4de3" : "#b0b0b0";
      return (
        <Icon
          key={type}
          style={{ color, marginRight: "8px", fontSize: "1.2rem" }}
        />
      );
    });
  };

  const totalPages = Math.ceil(totalPublicationsCount / itemsPerPage) || 1;


  const maxPagesToShow = 3;
  let startPage = Math.max(1, Math.min(currentPage - 2, totalPages - maxPagesToShow + 1));
  startPage = Math.max(1, startPage);
  const endPage = Math.min(startPage + maxPagesToShow - 1, totalPages);
  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  return (
    <SidebarTopbar>
      <div className="generations-history-container">
        <div className="tabs-container">
          <div
            className={`tab-item ${activeTab === "all" ? "active" : ""}`}
            onClick={() => handleTabClick("all")}
          >
            <FaList className="tab-icon" />
            <span className="tab-label">All</span>
          </div>

          {isTabAllowed("video") && (
            <div
              className={`tab-item ${activeTab === "abstractVideos" ? "active" : ""
                }`}
              onClick={() => handleTabClick("abstractVideos")}
            >
              <GrDocumentVideo className="tab-icon" />
              <span className="tab-label">Videos</span>
            </div>
          )}

          {isTabAllowed("ppt") && (
            <div
              className={`tab-item ${activeTab === "presentations" ? "active" : ""
                }`}
              onClick={() => handleTabClick("presentations")}
            >
              <GrDocumentPpt className="tab-icon" />
              <span className="tab-label">Presentations</span>
            </div>
          )}

          {isTabAllowed("poster") && (
            <div
              className={`tab-item ${activeTab === "posters" ? "active" : ""}`}
              onClick={() => handleTabClick("posters")}
            >
              <GrDocumentImage className="tab-icon" />
              <span className="tab-label">Posters</span>
            </div>
          )}

          {isTabAllowed("audio") && (
            <div
              className={`tab-item ${activeTab === "audioSummaries" ? "active" : ""
                }`}
              onClick={() => handleTabClick("audioSummaries")}
            >
              <GrDocumentSound className="tab-icon" />
              <span className="tab-label">Audio Summaries</span>
            </div>
          )}

          {isTabAllowed("carousel") && (
            <div
              className={`tab-item ${activeTab === "carousels" ? "active" : ""
                }`}
              onClick={() => handleTabClick("carousels")}
            >
              <BiCarousel className="tab-icon" />
              <span className="tab-label">Carousels</span>
            </div>
          )}

          {isTabAllowed("post") && (
            <div
              className={`tab-item ${activeTab === "Instapost" ? "active" : ""
                }`}
              onClick={() => handleTabClick("Instapost")}
            >
              <BiImageAlt className="tab-icon" />
              <span className="tab-label">Post</span>
            </div>
          )}
        </div>
        <div className="top-bar">
          <form onSubmit={handleSearchSubmit} className="search-form">
            <div className="search-input-container">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search here"
                value={searchQuery}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>
          </form>
        </div>

        <table className="publications-table">
          <colgroup>
            <col style={{ width: hideActionColumn ? "43%" : "40%" }} />
            <col style={{ width: hideActionColumn ? "22%" : "20%" }} />
            <col style={{ width: hideActionColumn ? "15%" : "13%" }} />
            <col style={{ width: hideActionColumn ? "15%" : "14%" }} />
            <col style={{ width: hideActionColumn ? "5%" : "13%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>PUBLICATION NAME</th>
              <th>TYPE</th>
              <th>FORMATS</th>
              <th>UPLOADED DATE</th>
              {!hideActionColumn ? <th>ACTION</th> : <th></th>}
            </tr>
          </thead>
          <tbody>
            {filteredPublications.map((pub) => (
              <tr key={pub.id}>
                <td
                  onClick={() => handlePreview(pub.id)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="publication-name">{pub.title}</div>
                </td>
                <td>
                  <span className="chip">{pub.type}</span>
                </td>
                <td>{renderFormatIcons(pub.UserFile, pub.task)}</td>
                <td>{pub.date}</td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div className="dropdown">
                      <button className="dropdown-toggle">⋮</button>
                      <div className="dropdown-menu">
                        <button onClick={() => handlePreview(pub.id)}>Preview</button>
                        <button onClick={() => handleDeleteClick(pub.id)}>Delete</button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>

        </table>

        <div className="pagination">
          <button
            className="page-button"
            onClick={() => {
              const newPage = Math.max(currentPage - 1, 1);
              setCurrentPage(newPage);
              fetchPublications(newPage, activeTab, searchQuery);
            }}
            disabled={currentPage <= 1}
          >
            <FaChevronLeft />
          </button>
          {pageNumbers.map((num) => (
            <button
              key={num}
              className={`page-button ${num === currentPage ? "active" : ""}`}
              onClick={() => {
                setCurrentPage(num);
                fetchPublications(num, activeTab, searchQuery);
              }}
            >
              {num}
            </button>
          ))}
          <button
            className="page-button"
            onClick={() => {
              const newPage = Math.min(currentPage + 1, totalPages);
              setCurrentPage(newPage);
              fetchPublications(newPage, activeTab, searchQuery);
            }}
            disabled={currentPage >= totalPages}
          >
            <FaChevronRight />
          </button>
        </div>

        {showDeleteModal && (
          <div className="delete-confirmation-modal">
            <div className="delete-confirmation-dialog">
              <h3>Are you sure you want to delete?</h3>
              <p>This action cannot be undone.</p>
              <div className="delete-confirmation-buttons">
                <button
                  className="confirm-delete-button"
                  onClick={handleConfirmDelete}
                >
                  Yes, Delete
                </button>
                <button
                  className="cancel-delete-button"
                  onClick={handleCancelDelete}
                >
                  No, Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteResultModal && (
          <div className="delete-confirmation-modal">
            <div className="delete-confirmation-dialog">
              <div style={{ fontSize: "3rem", marginBottom: "12px" }}>
                {deleteResultSuccess ? (
                  <FaCheckCircle style={{ color: "#1e4de3" }} />
                ) : (
                  <FaExclamationCircle style={{ color: "red" }} />
                )}
              </div>
              <h3>{deleteResultMessage}</h3>
              <div className="delete-confirmation-buttons">
                <button
                  className="confirm-delete-button"
                  onClick={handleCloseResultModal}
                >
                  Got It
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SidebarTopbar>
  );
}

export default GenerationsHistory;